import {parse as fontFaceSrcParser} from 'css-font-face-src';
import {
  composeValidations,
  ignoreNullish,
  validateFontFamily,
  validateFontWeight,
  validateNotNullish,
  validateObject,
  validateString,
} from '@stripe-internal/embedded-theming';
import {
  urlJoin,
  isDataUrl,
  isAbsoluteUrl,
  isAbsoluteUrlWithoutProtocol,
} from './urls';
import {
  validateFontDisplay,
  validateFontStyle,
  validateFontVariant,
  validateFontStretch,
  validateFontUnicodeRange,
} from './validateFonts';
import {getStronglyTypedEntries} from '../../utils/getStronglyTypedEntries';
import {StandardLogger} from '../../utils/getLogger';

/**
 * Referenced code from https://git.corp.stripe.com/stripe-internal/stripe-js-v3/blob/468a4e2f/src/elements/inner/shared/makeFontFace.ts#L1
 * In the context of embedded components, this code is run in the UI layer iframe when it is initialized by the platform frame.
 * It receives the loaded fonts from the platform frame, parses them using the `css-font-face-src` library,
 * and calls `sanitizeLoadedFont` on each font for its safe-transformation process, which resolves URLs, enforces HTTPS, and sanitizes risky values, etc.
 * After this, __resolveFontRelativeTo is no longer needed for generating CSS on the UI layer iframe.
 */

type LoadedFont = {
  family: string;
  src: string;
  __resolveFontRelativeTo: string;
  unicodeRange?: string;
  style?: string;
  variant?: string;
  stretch?: string;
  weight?: string;
  display?: string;
};
export type FontSrc = {
  loadType: 'local' | 'url';
  format?: string;
  value: string;
};
export type SafeFont = Omit<LoadedFont, '__resolveFontRelativeTo' | 'src'> & {
  src: FontSrc[];
};
const LOAD_TYPES: Record<FontSrc['loadType'], null> = {
  local: null,
  url: null,
};

/**
 * This function sanitizes the URL of the font-src (which is the `value` property in the FontSrc object). It ensures that only `https:`, `data:`, or `//` URLs are accepted.
 * @param str The value of the font-src property.
 * @returns The sanitized value of the font-src property.
 */
const sanitizeFontSrcURL = (str: string): string => {
  // Only allow URLs that start with `https://`, `data:`, or `//`.
  const matchProtocol =
    str.match(/^"?'?https:\/\//) || str.match(/^data:/) || str.match(/^\/\//);
  if (!matchProtocol) {
    throw new Error(
      `Invalid src value in font configuration: ${str}. URLs have to start with 'https://' or 'data:'.`,
    );
  }

  // We need to allow #?&= for querystrings and hashes
  // ; and , for data-URIs
  const matchCharacters = str.match(/^[#?&=;,a-zA-Z0-9-+_/.:]*$/);
  if (!matchCharacters) {
    throw new Error(
      `Invalid src value in font configuration: ${str}. This value contains invalid characters.`,
    );
  }

  // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
  return window.encodeURI(str);
};

/**
 * This function sanitizes the `format` property of the FontSrc object.
 * @param str The value of the `format` property of the FontSrc object.
 * @returns The sanitized value of the `format` property of the FontSrc object.
 */
const sanitizeFontSrcFormat = (str: string): string => {
  // Example values are woff2, svg, opentype.
  const match = str.match(/^[-a-zA-Z0-9]*$/);
  if (!match) {
    throw new Error(
      `Invalid src value in font configuration: ${str}. This value contains invalid characters.`,
    );
  }

  // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
  return window.encodeURI(str);
};

/**
 * Validates that the value argument is a FontSrc object.
 * @param value The argument to be validated.
 * @param path The path to the argument.
 */
function validateFontSrc(
  value: unknown,
  path: string,
): asserts value is FontSrc {
  const logWarning = StandardLogger.warn;
  validateObject(
    logWarning, // this doesn't really matter since we don't use it if shouldThrow is true
    {
      loadType: (value, path) => {
        if (typeof value !== 'string') {
          throw new Error(`${path} must be a string`);
        }

        if (!Object.keys(LOAD_TYPES).includes(value)) {
          throw new Error(
            `${path} must be one of ${Object.keys(LOAD_TYPES).join(', ')}`,
          );
        }
      },
      format: ignoreNullish(validateString),
      value: validateString,
    },
    undefined,
    true,
  )(value, path);
}

/**
 * This function sanitizes the parsed value from the `css-font-face-src` library.
 * @param value The parsed value from the `css-font-face-src` library.
 * @returns A FontSrc object.
 */
export const sanitizeFontSrc = (value: Record<string, unknown>[]): FontSrc[] =>
  value.map((unsafeFontSrc) => {
    validateFontSrc(unsafeFontSrc, String(unsafeFontSrc));

    // Sanitiize the `format` property
    if (unsafeFontSrc.format) {
      unsafeFontSrc.format = sanitizeFontSrcFormat(unsafeFontSrc.format);
    }

    // Sanitize the `value` property
    if (unsafeFontSrc.loadType === 'local') {
      validateFontFamily(unsafeFontSrc.value, unsafeFontSrc.value);
    } else {
      unsafeFontSrc.value = sanitizeFontSrcURL(unsafeFontSrc.value);
    }
    return unsafeFontSrc;
  });

const parseFontSrc = (src: string) => {
  try {
    return fontFaceSrcParser(src).reduce<Array<FontSrc>>(
      (aggr: Array<FontSrc>, {local, format, url}) => {
        if (local && typeof local === 'string') {
          const next: FontSrc = {
            loadType: 'local',
            value: local,
          };
          if (format) {
            next.format = format;
          }
          return [...aggr, next];
        }

        if (url && typeof url === 'string') {
          const next: FontSrc = {
            loadType: 'url',
            value: url,
          };
          if (format) {
            next.format = format;
          }
          return [...aggr, next];
        }

        return aggr;
      },
      [],
    );
  } catch (e: any) {
    throw new Error(`Invalid src value in font configuration: ${e.message}`);
  }
};

/**
 * Validates that the argument has the required properties of a loaded font that was passed from the platform frame to the UI layer iframe.
 * @param value The argument to be validated.
 * @param path The path to the argument.
 */
function validateLoadedFont(
  value: unknown,
  path: string,
): asserts value is LoadedFont {
  const logWarning = StandardLogger.warn;

  validateObject(
    logWarning, // this doesn't really matter since we don't use it if shouldThrow is true
    {
      src: composeValidations(
        validateNotNullish("Font object is missing a 'src' property"),
        validateString,
      ),
      family: composeValidations(
        validateNotNullish("Font object is missing a 'family' property"),
        validateString,
      ),
      __resolveFontRelativeTo: composeValidations(
        validateNotNullish(
          "Font object is missing a '__resolveFontRelativeTo' property",
        ),
        validateString,
      ),
      unicodeRange: ignoreNullish(validateFontUnicodeRange),
      style: ignoreNullish(validateFontStyle),
      variant: ignoreNullish(validateFontVariant),
      stretch: ignoreNullish(validateFontStretch),
      weight: ignoreNullish(validateFontWeight),
      display: ignoreNullish(validateFontDisplay),
    },
    undefined,
    true,
  )(value, path);
}
/**
 * Validates and sanitizes a loaded font object.
 * @param loadedFont A font object that was passed from the platform frame to the UI layer iframe.
 * @returns A SafeFont object.
 */
export const sanitizeLoadedFont = (loadedFont: unknown): SafeFont => {
  if (!loadedFont || typeof loadedFont !== 'object') {
    throw new Error(
      `Expected a valid font object in the fonts Array but got ${String(
        loadedFont,
      )}`,
    );
  }

  // Validate the shape of the loaded font object
  validateLoadedFont(loadedFont, 'font-property');

  // Use css-font-face-src to parse the `src` property
  const unsafeFontSrcArray = parseFontSrc(loadedFont.src).map((fragment) => {
    if (
      fragment.loadType === 'local' ||
      isDataUrl(fragment.value) ||
      isAbsoluteUrl(fragment.value) ||
      isAbsoluteUrlWithoutProtocol(fragment.value)
    ) {
      return fragment;
    }

    return {
      ...fragment,
      value: urlJoin(loadedFont.__resolveFontRelativeTo, fragment.value),
    };
  });

  const fontSrcResult = sanitizeFontSrc(unsafeFontSrcArray);

  const result: SafeFont = {
    family: loadedFont.family,
    src: fontSrcResult,
  };

  getStronglyTypedEntries(loadedFont).forEach(
    ([fontPropertyKey, fontPropertyValue]) => {
      if (
        fontPropertyKey === '__resolveFontRelativeTo' ||
        fontPropertyKey === 'src'
      ) {
        return;
      }

      if (fontPropertyValue) {
        result[fontPropertyKey] = fontPropertyValue;
      }
    },
  );

  return result;
};
